'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import Link from 'next/link';

interface GalleryImage {
  id: number;
  image: string;
  alt: string;
  category: string;
}

interface SuccessHighlight {
  id: number;
  name: string;
  achievement: string;
  image: string;
  alt: string;
  year: number;
}

const GalleryAndStories = () => {
  const galleryImages: GalleryImage[] = [
  {
    id: 1,
    image: '/assets/images/PHOTO-2024-08-03-10-39-40-1770453852336.jpg',
    alt: 'Athletes training on outdoor track at GO Speed Academy',
    category: 'Training'
  },
  {
    id: 2,
    image: '/assets/images/f9562bb5-c4ca-42e8-96c1-8ad46c6205b7-1771167656339.png',
    alt: 'Group fitness session at GO Speed Academy facility',
    category: 'Community'
  },
  {
    id: 3,
    image: '/assets/images/PHOTO-2024-11-16-20-03-55_2-1770454144693.jpg',
    alt: 'Young athletes celebrating achievement at academy',
    category: 'Achievement'
  },
  {
    id: 4,
    image: '/assets/images/PHOTO-2025-01-26-22-24-57-1769030269858.jpg',
    alt: 'Medal ceremony with coach and young athletes at stadium',
    category: 'Success'
  },
  {
    id: 5,
    image: '/assets/images/PHOTO-2024-08-03-10-39-40_3-1770453871817.jpg',
    alt: 'Indoor training facility with athletes working out',
    category: 'Facility'
  },
  {
    id: 6,
    image: '/assets/images/PHOTO-2024-11-16-20-17-44_2-1770484263374.jpg',
    alt: 'Athletes performing speed drills on track',
    category: 'Training'
  }];


  const successHighlights: SuccessHighlight[] = [
  {
    id: 1,
    name: 'Priya Sharma',
    achievement: 'Delhi State Champion - 100m Sprint',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_16f73f9d0-1767030526109.png',
    alt: 'Young Indian teenage girl in blue athletic wear holding gold medal on outdoor track',
    year: 2025
  },
  {
    id: 2,
    name: 'Rajesh Kumar',
    achievement: 'Lost 25kg, Transformed Life',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1927cf9a3-1763293370228.png",
    alt: 'Middle-aged Indian man in black athletic shirt smiling proudly in gym',
    year: 2024
  },
  {
    id: 3,
    name: 'Ananya Verma',
    achievement: 'National Silver Medal - SGFI',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1b7d14495-1765034796248.png',
    alt: 'Young female athlete celebrating with silver medal at national competition',
    year: 2024
  },
  {
    id: 4,
    name: 'Aarav Patel',
    achievement: 'From Screen Time to Team Captain',
    image: '/assets/images/PHOTO-2025-01-26-22-24-57-1769030269858.jpg',
    alt: 'Young athletes with coach at medal ceremony wearing medals and trophies',
    year: 2025
  }];


  return (
    <section className="w-full py-16 lg:py-24 bg-background">
      <div className="w-full px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-block px-4 py-2 bg-primary/10 rounded-full mb-6">
            <p className="text-primary font-heading font-bold text-sm uppercase tracking-wider">
              Gallery & Success Stories
            </p>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-primary mb-6 leading-tight">
            Our Journey in Pictures
          </h2>
          <p className="text-lg text-text-secondary font-body max-w-3xl mx-auto leading-relaxed">
            Witness the transformation, dedication, and achievements of our academy community through moments captured in training and competition.
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-16">
          {galleryImages.map((item) =>
          <div
            key={item.id}
            className="relative group overflow-hidden rounded-xl shadow-card hover:shadow-hover transition-all duration-300">
            
              <div className="relative aspect-square">
                <AppImage
                src={item.image}
                alt={item.alt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
              
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 mt-0" />
                <div className="absolute bottom-4 left-4 right-4 transform translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <span className="inline-block px-3 py-1 bg-accent rounded-full text-white text-xs font-heading font-bold uppercase">
                    {item.category}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Success Stories Highlights */}
        <div className="bg-muted rounded-2xl p-8 lg:p-12">
          <div className="text-center mb-10">
            <h3 className="text-2xl sm:text-3xl font-heading font-bold text-primary mb-4">
              Success Stories That Inspire
            </h3>
            <p className="text-base text-text-secondary font-body max-w-2xl mx-auto">
              Real people, real transformations, real results from our community
            </p>
          </div>

          <div className="flex flex-col items-center justify-center py-8">
            <div className="text-center">
              <p className="text-8xl sm:text-9xl lg:text-[10rem] font-heading font-bold text-accent leading-none">
                1000+
              </p>
              <p className="text-xl sm:text-2xl font-heading font-semibold text-primary mt-4">
                Success Stories & Counting
              </p>
              <p className="text-base text-text-secondary font-body mt-3 max-w-lg mx-auto">
                Athletes who transformed their lives through dedication, expert coaching, and the GO Speed community.
              </p>
            </div>
          </div>

          {/* CTA to Success Stories Page */}
          <div className="text-center mt-10">
            <Link
              href="/success-stories"
              className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-white rounded-lg font-heading font-bold hover:bg-accent/90 transition-all duration-300 hover:gap-3">
              
              View All Success Stories
              <Icon name="ArrowRightIcon" size={20} />
            </Link>
          </div>
        </div>

        {/* Stats Section */}
        <div className="mt-12 text-center">
          <div className="inline-flex flex-wrap items-center justify-center gap-8 px-8 py-6 bg-card rounded-2xl shadow-card">
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Icon name="TrophyIcon" size={32} className="text-[#FFD700]" variant="solid" />
                <p className="text-4xl font-heading font-bold text-primary">150+</p>
              </div>
              <p className="text-sm text-text-secondary font-body">Total Medals Won</p>
            </div>
            <div className="h-12 w-px bg-border hidden sm:block" />
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Icon name="StarIcon" size={32} className="text-accent" variant="solid" />
                <p className="text-4xl font-heading font-bold text-primary">15+</p>
              </div>
              <p className="text-sm text-text-secondary font-body">State Champions</p>
            </div>
            <div className="h-12 w-px bg-border hidden sm:block" />
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Icon name="UsersIcon" size={32} className="text-primary" variant="solid" />
                <p className="text-4xl font-heading font-bold text-primary">1000+</p>
              </div>
              <p className="text-sm text-text-secondary font-body">Success Stories</p>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default GalleryAndStories;